const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

const entry = (icon, tooltip, label = "") => Object.freeze({ icon, tooltip, label });

export const COMMAND_PRESENTATION = Object.freeze({
  paragraph: entry("text", "正文", "T"),
  "heading-1": entry("heading", "一级标题", "H1"),
  "heading-2": entry("heading", "二级标题", "H2"),
  "heading-3": entry("heading", "三级标题", "H3"),
  "heading-4": entry("heading", "四级标题", "H4"),
  "heading-5": entry("heading", "五级标题", "H5"),
  "ordered-list": entry("ordered-list", "有序列表"),
  "bullet-list": entry("bullet-list", "无序列表"),
  "task-list": entry("task-list", "任务列表"),
  "code-block": entry("code-block", "代码块"),
  blockquote: entry("quote", "引用"),
  "horizontal-rule": entry("horizontal-rule", "分隔线"),
  "align-left": entry("align-left", "左对齐"),
  "align-center": entry("align-center", "居中对齐"),
  "align-right": entry("align-right", "右对齐"),
  "indent-increase": entry("indent-increase", "增加缩进"),
  "indent-decrease": entry("indent-decrease", "减少缩进"),
  bold: entry("bold", "粗体", "B"),
  strike: entry("strike", "删除线", "S"),
  italic: entry("italic", "斜体", "I"),
  underline: entry("underline", "下划线", "U"),
  link: entry("link", "链接"),
  "inline-code": entry("inline-code", "行内代码"),
  "text-color": entry("text-color", "文字颜色"),
  "background-color": entry("background-color", "背景颜色"),
  "reset-colors": entry("reset-colors", "恢复默认颜色"),
  "clear-formatting": entry("clear-formatting", "清除格式"),
  "block-cut": entry("cut", "剪切"),
  "block-copy": entry("copy", "复制"),
  "block-delete": entry("delete", "删除"),
  ai: entry("sparkles", "AI 改写", "AI")
});

export const SELECTION_GROUPS = Object.freeze([
  Object.freeze({
    id: "structure",
    icon: "text",
    tooltip: "段落与标题",
    commands: Object.freeze([
      "paragraph", "heading-1", "heading-2", "heading-3", "heading-4", "heading-5"
    ])
  }),
  Object.freeze({
    id: "blocks",
    icon: "bullet-list",
    tooltip: "列表与块类型",
    commands: Object.freeze([
      "ordered-list", "bullet-list", "task-list", "code-block", "blockquote"
    ])
  }),
  Object.freeze({
    id: "alignment",
    icon: "align-left",
    tooltip: "对齐与缩进",
    commands: Object.freeze([
      "align-left", "align-center", "align-right", "indent-increase", "indent-decrease"
    ])
  }),
  Object.freeze({
    id: "colors",
    icon: "text-color",
    tooltip: "文字与背景颜色",
    commands: Object.freeze(["text-color", "background-color", "reset-colors"])
  })
]);

const ICON_PATHS = Object.freeze({
  text: ["M5 6h14", "M12 6v14", "M8 20h8"],
  heading: ["M5 5v14", "M19 5v14", "M5 12h14"],
  "ordered-list": [
    "M10 6h10", "M10 12h10", "M10 18h10",
    "M4 5h2v3", "M4 11h2l-2 3h2", "M4 17h2v3H4"
  ],
  "bullet-list": [
    "M9 6h11", "M9 12h11", "M9 18h11",
    "M4 6h.01", "M4 12h.01", "M4 18h.01"
  ],
  "task-list": [
    "M9 6h11", "M9 12h11", "M9 18h11",
    "M3 6l1.5 1.5L7 4", "M3 12l1.5 1.5L7 10", "M3 18l1.5 1.5L7 16"
  ],
  "code-block": ["M8 9l-3 3 3 3", "M16 9l3 3-3 3", "M14 5l-4 14"],
  quote: ["M7 9H4v6h5V9H7z", "M17 9h-3v6h5V9h-2z"],
  "horizontal-rule": ["M4 12h16"],
  "align-left": ["M4 6h16", "M4 10h11", "M4 14h16", "M4 18h11"],
  "align-center": ["M4 6h16", "M7 10h10", "M4 14h16", "M7 18h10"],
  "align-right": ["M4 6h16", "M9 10h11", "M4 14h16", "M9 18h11"],
  "indent-increase": ["M10 6h10", "M10 12h10", "M10 18h10", "M4 9l3 3-3 3"],
  "indent-decrease": ["M10 6h10", "M10 12h10", "M10 18h10", "M7 9l-3 3 3 3"],
  bold: ["M7 5h6a4 4 0 0 1 0 8H7z", "M7 13h7a3 3 0 0 1 0 6H7z"],
  strike: [
    "M5 12h14",
    "M8 7.5A4 4 0 0 1 12 5c2.2 0 4 1 4 3",
    "M8 16c.7 2 2.1 3 4.5 3 2.5 0 4.5-1.3 4.5-3.5"
  ],
  italic: ["M10 5h7", "M7 19h7", "M14 5L10 19"],
  underline: ["M7 5v6a5 5 0 0 0 10 0V5", "M5 20h14"],
  link: [
    "M10 13a5 5 0 0 0 7.1.1l2-2a5 5 0 0 0-7.1-7.1l-1.1 1.1",
    "M14 11a5 5 0 0 0-7.1-.1l-2 2A5 5 0 0 0 12 20l1.1-1.1"
  ],
  "inline-code": ["M8 9l-3 3 3 3", "M16 9l3 3-3 3"],
  "text-color": ["M6 19l6-14 6 14", "M8.5 14h7", "M5 22h14"],
  "background-color": ["M5 18l5-12 8 8-6 6H5z", "M14 19h6"],
  "reset-colors": ["M4 12a8 8 0 1 0 2-5.3", "M4 4v5h5"],
  "clear-formatting": ["M5 5h9l5 5-7 7H7l-3-3 8-9", "M4 20h16"],
  cut: [
    "M4 4l16 16", "M20 4L8 16",
    "M6 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6",
    "M18 15a3 3 0 1 0 0 6 3 3 0 0 0 0-6"
  ],
  copy: ["M8 8h11v11H8z", "M5 16H4V4h12v1"],
  delete: [
    "M4 7h16", "M9 7V4h6v3", "M7 7l1 13h8l1-13",
    "M10 11v5", "M14 11v5"
  ],
  more: ["M5 12h.01", "M12 12h.01", "M19 12h.01"],
  sparkles: [
    "M12 3l1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2L12 3z",
    "M19 15l.7 2.3L22 18l-2.3.7L19 21l-.7-2.3L16 18l2.3-.7L19 15z"
  ]
});

const BLOCK_COMMAND_IDS = Object.freeze({
  paragraph: "paragraph",
  orderedList: "ordered-list",
  bulletList: "bullet-list",
  taskList: "task-list",
  codeBlock: "code-block",
  blockquote: "blockquote",
  horizontalRule: "horizontal-rule"
});

export function createCommandIcon(document, idOrIcon) {
  const presentationIcon = COMMAND_PRESENTATION[idOrIcon]?.icon;
  const iconId = ICON_PATHS[idOrIcon]
    ? idOrIcon
    : presentationIcon && ICON_PATHS[presentationIcon]
      ? presentationIcon
      : "more";
  const svg = document.createElementNS(SVG_NAMESPACE, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "1.8");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  for (const data of ICON_PATHS[iconId]) {
    const path = document.createElementNS(SVG_NAMESPACE, "path");
    path.setAttribute("d", data);
    svg.append(path);
  }
  return svg;
}

export function getBlockTriggerPresentation(block) {
  if (block?.type === "heading") {
    const level = Number(block.level);
    const id = level >= 1 && level <= 5 ? `heading-${level}` : "paragraph";
    return { ...COMMAND_PRESENTATION[id] };
  }
  const id = BLOCK_COMMAND_IDS[block?.type] || "paragraph";
  return { ...COMMAND_PRESENTATION[id] };
}
